'use client';

import { useEffect, useState, memo } from 'react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import styles from '@/styles/components/SwiperComponent.module.scss';

interface SwiperComponentProps {
  items: any[];
  slidesPerViewDesktop?: number;
  slidesPerViewMobile?: number;
  centeredSlides?: boolean;
  contorlButtons?: boolean;
  autoPlay?: boolean;
}

// Custom Navigation Button for Next Slide
const SwiperButtonNext = memo(({ isEnd }: { isEnd: boolean }) => {
  const swiper = useSwiper();

  return (
    <button
      onClick={() => {
        if (isEnd) {
          swiper.slideTo(0); // Wrap around to the first slide
        } else {
          swiper.slideNext();
        }
      }}
      aria-label="Next"
      className={`${styles.btn__next} ${isEnd ? styles.disabled : ''}`}
      disabled={isEnd}
    />
  );
});

// Custom Navigation Button for Previous Slide
const SwiperButtonPrev = memo(({ isBeginning }: { isBeginning: boolean }) => {
  const swiper = useSwiper();

  return (
    <button
      onClick={() => {
        if (isBeginning) {
          swiper.slideTo(swiper.slides.length - 1); // Wrap around to the last slide
        } else {
          swiper.slidePrev();
        }
      }}
      aria-label="Previous"
      className={`${styles.btn__prev} ${isBeginning ? styles.disabled : ''}`}
      disabled={isBeginning}
    />
  );
});

// Swiper Component
const SwiperComponent = ({
  items,
  slidesPerViewDesktop = 3.5,
  slidesPerViewMobile = 1.2,
  centeredSlides = false,
  contorlButtons = true,
  autoPlay = false,
}: SwiperComponentProps) => {
  const [slidesPerView, setSlidesPerView] = useState<number>(slidesPerViewDesktop);
  const [isBeginning, setIsBeginning] = useState<boolean>(true);
  const [isEnd, setIsEnd] = useState<boolean>(false);

  // Handle responsive slidesPerView
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      setSlidesPerView(isMobile ? slidesPerViewMobile : slidesPerViewDesktop);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!items || items.length === 0) {
    return <p className="no-items">No items to display</p>;
  }

  return (
    <Swiper
      modules={[Navigation, Pagination, A11y]}
      spaceBetween={24}
      centeredSlides={centeredSlides}
      slidesPerView={slidesPerView}
      autoplay={autoPlay ? { delay: 5000 } : undefined}
      onSlideChange={(swiper) => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
      }}
      onReachBeginning={() => setIsBeginning(true)}
      onReachEnd={() => setIsEnd(true)}
      onFromEdge={() => {
        setIsBeginning(false);
        setIsEnd(false);
      }}
      aria-live="polite"
    >
      {(contorlButtons && slidesPerView < items.length) &&
        <div className={styles.navigation}>
          <SwiperButtonPrev isBeginning={isBeginning} />
          <SwiperButtonNext isEnd={isEnd} />
        </div>
      }

      {/* Slides */}
      {items.map((item, index) => (
        <SwiperSlide key={index} onClick={(i) => {
          console.log('Slide clicked:', item.key);
        }}>
          {item}
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default SwiperComponent;